import { Button, Card } from '@citizen-walk/shared-ui';
import type { VerificationTemplatePreset } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { readPresetTemplate, selectImportablePresets } from './useTemplateCopyLink';

/**
 * Master preset → 地区 検証テンプレート 取込 modal
 * (Sprint BG-DataSettings 13、GAP-A11 / A-S24 / A-S25)。
 *
 * 旧 v1 `old/apps/admin/src/pages/VerificationTemplatesPage.tsx` の 「マスターから取り込み」UI を
 * dialog 化して再利用可能に分離 (v2 では VerificationTemplatesAdminPage 内で使う)。
 * Survey 版 `SurveyImportFromPresetModal` と pattern 1:1。
 *
 * - 一覧: published + enabled + 未 archive な preset のみ表示
 * - 各 row に「📋 copy で取込」「🔗 link で取込」buttons
 * - 親 component (VerificationTemplatesAdminPage) が onCopy / onLink callback で実行
 * - 取込中 (busy) は button disabled、二重実行防止
 * - 取込成功時は親が onClose を呼んで modal を閉じる
 */

export interface VerificationTemplateImportFromPresetModalProps {
  /** master preset 配列 (useVerificationTemplatePresets() からそのまま渡す)。 */
  presets: Array<VerificationTemplatePreset & { id: string }>;
  /** loading 状態 (presets 読込中なら true)。 */
  loading: boolean;
  /** 取込中フラグ (親が制御、true の間 button disabled)。 */
  busy: boolean;
  /** copy で取込 (forked、独立)。 */
  onCopy: (preset: VerificationTemplatePreset & { id: string }) => void | Promise<void>;
  /** link で取込 (linked、追従)。 */
  onLink: (preset: VerificationTemplatePreset & { id: string }) => void | Promise<void>;
  /** modal close (overlay click / cancel button)。 */
  onClose: () => void;
}

export function VerificationTemplateImportFromPresetModal(
  props: VerificationTemplateImportFromPresetModalProps,
) {
  const importables = selectImportablePresets(props.presets);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="verification-template-import-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2
            id="verification-template-import-modal-title"
            className="text-lg font-bold text-slate-900"
          >
            📥 マスター preset から取込
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="閉じる"
            className="text-slate-500 hover:text-slate-700 text-2xl leading-none"
          >
            ×
          </button>
        </header>

        <div className="p-5">
          <p className="text-xs text-slate-600 mb-4">
            <strong>📋 copy</strong>: preset を deep clone (以後独立、自由編集可) /{' '}
            <strong>🔗 link</strong>: preset を参照 (再リンクで最新化、別 sprint で再リンク UI)
          </p>

          {props.loading && (
            <Card padding="md">
              <p className="text-sm text-slate-500">preset 読込中…</p>
            </Card>
          )}

          {!props.loading && importables.length === 0 && (
            <Card padding="md">
              <p className="text-sm text-slate-600">
                取込可能な preset がありません (published + enabled + 未 archive)。
              </p>
            </Card>
          )}

          {!props.loading && importables.length > 0 && (
            <ul className="grid grid-cols-1 gap-2">
              {importables.map((preset) => {
                const isSelected = selectedId === preset.id;
                const body = readPresetTemplate(preset);
                const perPostCount = body.perPostQuestions?.length ?? 0;
                const perClsCount = body.perClassificationQuestions?.length ?? 0;
                const targetModeLabel = body.targetMode ?? '全モード';
                const importedDisplayName = body.displayName?.trim() || preset.displayName;
                return (
                  <li key={preset.id}>
                    <Card
                      padding="md"
                      className={isSelected ? 'border-primary-400' : 'border-slate-200'}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
                              {preset.mode}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                              対象: {targetModeLabel}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                              投稿 {perPostCount} 問
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                              分類 {perClsCount} 問
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                              v{preset.version}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-slate-900 truncate">
                            {preset.displayName}
                          </p>
                          {body.displayName && body.displayName !== preset.displayName && (
                            <p className="text-xs text-slate-500 truncate">
                              取込時 displayName: {importedDisplayName}
                            </p>
                          )}
                          {body.description && (
                            <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                              {body.description}
                            </p>
                          )}
                          <p className="mt-1 text-[10px] text-slate-400">
                            id: <code>{preset.id}</code>
                          </p>
                        </div>
                        <div className="shrink-0 flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={props.busy}
                            onClick={() => {
                              setSelectedId(preset.id);
                              void props.onCopy(preset);
                            }}
                            title="copy: deep clone で独立した検証テンプレ化"
                          >
                            📋 copy で取込
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={props.busy}
                            onClick={() => {
                              if (
                                // eslint-disable-next-line no-alert
                                !confirm(
                                  `「${preset.displayName}」を link として取り込みます。\n` +
                                    'master の更新は手動「再リンク」時に反映されます (再リンク UI は別 sprint)。',
                                )
                              )
                                return;
                              setSelectedId(preset.id);
                              void props.onLink(preset);
                            }}
                            title="link: preset 参照で取込 (再リンクで最新化)"
                          >
                            🔗 link で取込
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end">
          <Button variant="secondary" size="md" onClick={props.onClose} disabled={props.busy}>
            キャンセル
          </Button>
        </footer>
      </div>
    </div>
  );
}
